import moment from 'moment-timezone';

// Same picklist / codes as the backend (src/utils/deactivationReasons.js). Order = display order.
export const DEACTIVATION_REASONS = [
    { code: 'deceased', label: 'Deceased' },
    { code: 'moved', label: 'Moved / relocated' },
    { code: 'changed_provider', label: 'Changed provider / left practice' },
    { code: 'declined_consent', label: 'Declined / withdrew consent' },
    { code: 'hospice', label: 'Hospice' },
    { code: 'long_term_care', label: 'Long-term care / skilled nursing facility' },
    { code: 'lost_insurance', label: 'Lost insurance / not eligible' },
    { code: 'non_compliant', label: 'Not using device / non-compliant' },
    { code: 'unable_to_contact', label: 'Unable to contact' },
    { code: 'duplicate_record', label: 'Duplicate record' },
    { code: 'enrolled_in_error', label: 'Enrolled in error' },
    { code: 'other', label: 'Other' },
];
export const NOT_RECORDED = 'Not recorded';
export const NOT_RECORDED_CODE = 'not_recorded';
export const NOTE_MAX = 500;
export const OTHER_TEXT_MAX = 200;
export const DECEASED_REACTIVATE_CONFIRM = 'Reactivate a patient marked deceased?';

export const reasonLabel = (code, text) => {
    const r = DEACTIVATION_REASONS.find((x) => x.code === code);
    if (!r) return NOT_RECORDED;
    return code === 'other' && text ? `Other: ${text}` : r.label;
};

export const todayEt = () => moment().tz('America/New_York').format('YYYY-MM-DD');

export const lastFirst = (p) => [p?.lastname, p?.firstname].filter(Boolean).join(', ');

export const deactivateConfirmText = (patient, code, text) =>
    `Deactivate ${lastFirst(patient)}? Reason: ${reasonLabel(code, (text || '').trim())}.`;

export const isDeceasedPatient = (p) =>
    !!p && (p.deceased === true || p.deactivationReasonCode === 'deceased' || p.deactivation?.deceased === true);

// Super Admin = role admin with no organization scope (same rule as the backend).
export const isSuperAdmin = (user) => user?.role === 'admin' && !(user?.organizationIds || []).length;

/** Client-side validation mirroring the server; returns an error message or null. */
export const validateDeactivation = ({ reasonCode, reasonText, note, effectiveDate, dateOfDeath }) => {
    if (!reasonCode) return 'Select a reason';
    if (reasonCode === 'other' && (reasonText || '').trim().length < 2) return 'Describe the reason for "Other"';
    if ((reasonText || '').length > OTHER_TEXT_MAX) return `Reason text is limited to ${OTHER_TEXT_MAX} characters`;
    if ((note || '').length > NOTE_MAX) return `Note is limited to ${NOTE_MAX} characters`;
    const today = todayEt();
    if (effectiveDate && effectiveDate > today) return 'Effective date cannot be in the future';
    if (dateOfDeath && dateOfDeath > today) return 'Date of death cannot be in the future';
    return null;
};

/** Request body for POST /patient/block/:id (block=true). */
export const deactivationPayload = ({ reasonCode, reasonText, note, effectiveDate, dateOfDeath }) => ({
    block: true,
    reasonCode,
    ...(reasonCode === 'other' && { reasonText: (reasonText || '').trim() }),
    ...((note || '').trim() && { note: note.trim() }),
    ...(effectiveDate && { effectiveDate }),
    ...(reasonCode === 'deceased' && dateOfDeath && { dateOfDeath }),
});

/** Counts by reason code (null/absent => not_recorded), in picklist order then Not recorded. */
export const countByReason = (rows = []) => {
    const counts = {};
    rows.forEach((p) => {
        const code = p?.deactivation?.reasonCode || NOT_RECORDED_CODE;
        counts[code] = (counts[code] || 0) + 1;
    });
    return [...DEACTIVATION_REASONS.map((r) => r.code), NOT_RECORDED_CODE]
        .filter((code) => counts[code])
        .map((code) => ({ code, label: code === NOT_RECORDED_CODE ? NOT_RECORDED : reasonLabel(code), count: counts[code] }));
};
